import { createFileRoute, Link } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GuestListRequestApi,
  GuestListRequest,
  GuestListRequestNotFoundError,
  GuestListRequestAlreadyPaidError,
} from '@/lib/GuestListRequestApi';
import { SelectPaymentMethod } from '@/components/purchase/checkout/SelectPaymentMethod';
import { Button } from '@/components/ui/button';
import { Money } from '@/lib/money';
import { useLanguage } from '@/providers/LanguageProvider';
import { RiCheckLine, RiLoader4Line } from '@remixicon/react';
import { calculateFee } from '@/lib/calculateFee';
import { handleRedirect } from '@/lib/iframeRedirect';

const darkShopStyle = {
  '--shop-textColor': '#ffffff',
  '--shop-panelBackgroundColor': '#0a0a0a',
  '--shop-borderColor': 'rgba(255,255,255,0.1)',
  '--shop-ctaButtonColor': '#ffffff',
  '--shop-ctaButtonTextColor': '#000000',
} as const;

const POLL_INTERVAL_MS = 2000;
const POST_PSP_POLL_TIMEOUT_MS = 60_000;
const ORDER_POLL_TIMEOUT_MS = 10_000;
const AWAITING_RETURN_KEY = (id: string) => `gl-request-awaiting-psp:${id}`;

function ShopStyleProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    Object.entries(darkShopStyle).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
    return () => {
      Object.keys(darkShopStyle).forEach((key) => {
        document.documentElement.style.removeProperty(key);
      });
    };
  }, []);
  return <>{children}</>;
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <ShopStyleProvider>
      <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
          {children}
          <div className="mt-8 sm:mt-16 pt-4 sm:pt-8 border-t border-white/10 text-center">
            <a
              href="https://fastlane.events"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block opacity-75 hover:opacity-100 transition-opacity"
            >
              <p className="text-xs sm:text-sm text-white/60 mb-2 sm:mb-3">
                Powered by Fastlane
              </p>
            </a>
          </div>
        </div>
      </div>
    </ShopStyleProvider>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="backdrop-blur-sm bg-white/5 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl border border-white/10">
      {children}
    </div>
  );
}

function InvalidLink() {
  return (
    <PageShell>
      <Card>
        <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-white">
          This link is no longer valid
        </h1>
        <p className="text-white/70 mb-4">
          We couldn't find this guestlist request. Your request may still be
          awaiting approval, the link may have expired, or the approval may
          have been revoked.
        </p>
        <p className="text-white/70">
          If you believe this is an error, please contact support at{' '}
          <a
            href="mailto:ticketing@fastlane.events"
            className="text-white underline hover:text-white/80"
          >
            ticketing@fastlane.events
          </a>
          .
        </p>
      </Card>
    </PageShell>
  );
}

export const Route = createFileRoute('/guestlist-request/$requestId')({
  component: GuestListRequestPaymentPage,
  errorComponent: InvalidLink,
});

type PageState =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'confirming_payment'; request: GuestListRequest } // post-PSP poll window
  | { kind: 'payment_form'; request: GuestListRequest }
  | { kind: 'payment_failed'; request: GuestListRequest }
  | { kind: 'awaiting_order'; request: GuestListRequest } // paid but no orderId yet
  | { kind: 'paid'; request: GuestListRequest }
  | { kind: 'order_stuck'; request: GuestListRequest };

function GuestListRequestPaymentPage() {
  const { requestId } = Route.useParams();
  const { currentLanguage: lang } = useLanguage();

  const api = useMemo(
    () => new GuestListRequestApi(import.meta.env.VITE_API_URL),
    [],
  );

  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stable refs so polling effects don't stampede when state updates.
  const pollStartedAt = useRef<number | null>(null);
  const mountedAt = useRef<number>(Date.now());

  const isAwaitingReturn = useCallback(() => {
    try {
      return sessionStorage.getItem(AWAITING_RETURN_KEY(requestId)) === '1';
    } catch {
      return false;
    }
  }, [requestId]);

  const setAwaitingReturn = useCallback(
    (value: boolean) => {
      try {
        if (value) {
          sessionStorage.setItem(AWAITING_RETURN_KEY(requestId), '1');
        } else {
          sessionStorage.removeItem(AWAITING_RETURN_KEY(requestId));
        }
      } catch {
        /* ignore */
      }
    },
    [requestId],
  );

  const resolveNextState = useCallback(
    (data: GuestListRequest, prev: PageState): PageState => {
      if (data.paymentStatus === 'paid') {
        if (data.orderId) {
          setAwaitingReturn(false);
          return { kind: 'paid', request: data };
        }
        // Paid but order not yet created — poll briefly.
        if (prev.kind === 'awaiting_order' || prev.kind === 'order_stuck') {
          if (Date.now() - mountedAt.current > ORDER_POLL_TIMEOUT_MS) {
            return { kind: 'order_stuck', request: data };
          }
        }
        return { kind: 'awaiting_order', request: data };
      }

      if (data.paymentStatus === 'failed') {
        setAwaitingReturn(false);
        return { kind: 'payment_failed', request: data };
      }

      if (data.paymentStatus === 'pending') {
        if (isAwaitingReturn()) {
          // Time out into failed state if we've been spinning too long.
          if (
            pollStartedAt.current !== null &&
            Date.now() - pollStartedAt.current > POST_PSP_POLL_TIMEOUT_MS
          ) {
            setAwaitingReturn(false);
            return { kind: 'payment_failed', request: data };
          }
          return { kind: 'confirming_payment', request: data };
        }
        return { kind: 'payment_form', request: data };
      }

      // not_required should never hit this page.
      return { kind: 'payment_failed', request: data };
    },
    [isAwaitingReturn, setAwaitingReturn],
  );

  const fetchOnce = useCallback(async () => {
    try {
      const data = await api.getRequest(requestId);
      setState((prev) => resolveNextState(data, prev));
    } catch (e) {
      if (e instanceof GuestListRequestNotFoundError) {
        setState({ kind: 'not_found' });
      } else {
        console.error('Failed to load guestlist request:', e);
        setError('Failed to load your request. Please try again.');
      }
    }
  }, [api, requestId, resolveNextState]);

  // Initial load.
  useEffect(() => {
    mountedAt.current = Date.now();
    if (isAwaitingReturn()) {
      pollStartedAt.current = Date.now();
    }
    fetchOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  // Drive polling while we need it.
  useEffect(() => {
    const needsPolling =
      state.kind === 'confirming_payment' || state.kind === 'awaiting_order';
    if (!needsPolling) return;

    const interval = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [state.kind, fetchOnce]);

  if (state.kind === 'loading') {
    return (
      <PageShell>
        <Card>
          <div className="text-center py-8">
            <div className="flex justify-center mb-6">
              <RiLoader4Line className="w-12 h-12 text-white/70 animate-spin" />
            </div>
            <p className="text-white/70">Loading your request…</p>
          </div>
        </Card>
      </PageShell>
    );
  }

  if (state.kind === 'not_found') {
    return <InvalidLink />;
  }

  const { request } = state;

  if (state.kind === 'confirming_payment') {
    return (
      <PageShell>
        <Card>
          <div className="text-center py-8">
            <div className="flex justify-center mb-6">
              <RiLoader4Line className="w-12 h-12 text-white/70 animate-spin" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold mb-2 text-white">
              Confirming your payment…
            </h1>
            <p className="text-white/70 text-sm sm:text-base">
              This usually only takes a few seconds. Please don't close this
              window.
            </p>
          </div>
        </Card>
      </PageShell>
    );
  }

  if (state.kind === 'awaiting_order') {
    return (
      <PageShell>
        <Card>
          <div className="text-center py-8">
            <div className="flex justify-center mb-6">
              <RiLoader4Line className="w-12 h-12 text-white/70 animate-spin" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold mb-2 text-white">
              Finalising your ticket…
            </h1>
            <p className="text-white/70 text-sm sm:text-base">
              Your payment was received. We're preparing your ticket now.
            </p>
          </div>
        </Card>
      </PageShell>
    );
  }

  if (state.kind === 'order_stuck') {
    return (
      <PageShell>
        <Card>
          <h1 className="text-2xl sm:text-3xl font-bold mb-4 text-white">
            Payment received
          </h1>
          <p className="text-white/70 mb-4">
            Thanks, {request.name}. Your payment went through, but creating
            your ticket is taking longer than expected. Your tickets will be
            emailed to {request.email} as soon as they're ready.
          </p>
          <p className="text-white/70">
            If you don't receive them shortly, please contact support at{' '}
            <a
              href="mailto:ticketing@fastlane.events"
              className="text-white underline hover:text-white/80"
            >
              ticketing@fastlane.events
            </a>
            .
          </p>
        </Card>
      </PageShell>
    );
  }

  if (state.kind === 'paid') {
    return (
      <PageShell>
        <Card>
          <div className="text-center py-6">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-full bg-green-500/20 border border-green-400/30 flex items-center justify-center">
                <RiCheckLine className="w-8 h-8 text-green-300" />
              </div>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2 text-white">
              Your spot is confirmed
            </h1>
            <p className="text-white/70 mb-6">
              Thanks, {request.name}. Your ticket for {request.eventName} is on
              its way to {request.email}.
            </p>
            {request.orderId && (
              <Link
                to="/order/$orderId"
                params={{ orderId: request.orderId }}
                search={{ invoiceSequenceId: undefined }}
                className="inline-block bg-white hover:bg-white/90 text-black font-semibold px-6 py-3 rounded-lg transition-colors text-sm sm:text-base"
              >
                View your order
              </Link>
            )}
          </div>
        </Card>
      </PageShell>
    );
  }

  // payment_form or payment_failed — need fees/methods/total from response.
  const totalAmount = request.totalAmount
    ? Money.fromInteger(
        request.totalAmount.amount,
        request.totalAmount.currency,
      )
    : null;
  const paymentMethods = request.paymentMethods ?? [];
  const appliedFees = request.appliedFees ?? [];

  const selectedMethod = paymentMethod
    ? paymentMethods.find((m) => m.id === paymentMethod) ?? null
    : null;

  const selectedPaymentMethodFee =
    selectedMethod && totalAmount
      ? calculateFee(selectedMethod.fee, totalAmount, 1)
      : null;

  const grandTotal =
    totalAmount &&
    selectedPaymentMethodFee &&
    !selectedPaymentMethodFee.isZero()
      ? totalAmount.add(selectedPaymentMethodFee)
      : totalAmount;

  const handlePayment = async () => {
    if (!paymentMethod) return;
    setPaying(true);
    setError(null);
    try {
      const response = await api.pay(
        requestId,
        paymentMethod,
        window.location.href,
      );
      if (response.type === 'redirect') {
        setAwaitingReturn(true);
        handleRedirect(response.url);
      }
    } catch (e) {
      if (e instanceof GuestListRequestAlreadyPaidError) {
        // Server says it's already paid — re-fetch so we render the paid state.
        setAwaitingReturn(false);
        await fetchOnce();
        setPaying(false);
        return;
      }
      console.error('Payment failed:', e);
      setError(
        'Payment failed. Please try again or contact support if the problem persists.',
      );
      setPaying(false);
    }
  };

  const isRetry = state.kind === 'payment_failed';

  return (
    <PageShell>
      <div className="text-center mb-8 sm:mb-12">
        <h2 className="text-xs sm:text-sm font-medium text-white/70 uppercase tracking-[0.2em] mb-2 sm:mb-3">
          Guestlist Payment
        </h2>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 text-white">
          {request.eventName}
        </h1>
        <p className="text-white/70 text-sm sm:text-base">
          Hi {request.name} — your request for {request.formName} has been
          approved. Complete your payment below to confirm your spot.
        </p>
      </div>

      <Card>
        {isRetry && (
          <div className="mb-4 sm:mb-6 p-3 rounded-lg bg-yellow-500/10 border border-yellow-400/30 text-yellow-200 text-sm">
            Your last payment didn't complete. Pick a method and try again.
          </div>
        )}

        {totalAmount && (
          <div className="mb-6">
            <div className="text-sm sm:text-base text-white/80 uppercase tracking-wider font-medium mb-3">
              Summary
            </div>

            {appliedFees.map((applied, idx) => {
              const perUnit = applied.fee.amountPerUnit;
              const lineAmount = Money.fromInteger(
                applied.amount.amount,
                applied.amount.currency,
              );
              return (
                <div
                  key={idx}
                  className="flex justify-between items-center py-2.5 border-b border-white/10"
                >
                  <div className="min-w-0">
                    <div className="text-white truncate">
                      {applied.fee.name}
                    </div>
                    {perUnit && request.places > 1 && (
                      <div className="text-xs text-white/60">
                        {Money.fromInteger(
                          perUnit.amount,
                          perUnit.currency,
                        ).display(lang)}{' '}
                        × {request.places} spots
                      </div>
                    )}
                  </div>
                  <div className="text-white font-medium shrink-0">
                    {lineAmount.display(lang)}
                  </div>
                </div>
              );
            })}

            {selectedPaymentMethodFee &&
              !selectedPaymentMethodFee.isZero() && (
                <div className="flex justify-between items-center py-2.5 border-b border-white/10">
                  <div className="text-sm text-white/70">
                    {selectedMethod?.fee.name}
                  </div>
                  <div className="text-white font-medium">
                    {selectedPaymentMethodFee.display(lang)}
                  </div>
                </div>
              )}

            <div className="flex justify-between items-center pt-3 mt-1">
              <div className="text-white/80 text-sm sm:text-base">Total</div>
              <div className="text-white font-semibold text-base sm:text-lg">
                {grandTotal?.display(lang)}
              </div>
            </div>
          </div>
        )}

        {request.feeDescription && (
          <p className="text-xs sm:text-sm text-white/60 mb-6">
            {request.feeDescription}
          </p>
        )}

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-900/30 border border-red-500/30 text-red-200 text-sm">
            {error}
          </div>
        )}

        {paymentMethods.length > 0 && totalAmount && (
          <div className="mb-6">
            <div className="text-sm sm:text-base text-white/80 uppercase tracking-wider font-medium mb-3">
              Payment method
            </div>
            <SelectPaymentMethod
              paymentMethods={paymentMethods}
              selectedMethod={paymentMethod}
              onMethodChange={setPaymentMethod}
              totalAmount={totalAmount}
            />
          </div>
        )}

        <Button
          type="button"
          disabled={!paymentMethod || paying}
          onClick={handlePayment}
          className="w-full bg-white hover:bg-white/90 text-black font-semibold py-4 sm:py-6 rounded-lg transition-all duration-300 text-sm sm:text-base"
        >
          {paying ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Processing…
            </>
          ) : grandTotal ? (
            `${isRetry ? 'Retry payment' : 'Pay'} ${grandTotal.display(lang)}`
          ) : (
            'Continue'
          )}
        </Button>

        <div className="text-xs sm:text-sm text-white/60 pt-4">
          After payment your ticket will be sent to your email: {request.email}
        </div>
      </Card>
    </PageShell>
  );
}
